/**
 * /projects/:projectId — a project's detail page. Shows the project header
 * and lists its pipelines with their lifecycle state, plus a link to create
 * a new pipeline.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPencil, IconPlus, IconShare } from "@tabler/icons-react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  getProject,
  lifecycleColor,
  listPipelines,
  renameProject,
  type PipelineOut,
  type ProjectOut,
} from "@/shared/api/projects";
import { ShareDialog } from "@/features/sharing/ShareDialog";
import { useMe } from "@/features/users/useMe";
import { RenameModal } from "./RenameModal";
import { listCardActionStyle, listCardStyle } from "@/shared/ui/listCard";
import { createdOn, creator } from "./creatorMeta";

export function ProjectDetail() {
  const { projectId = "" } = useParams();
  const getToken = useAccessToken();
  const [project, setProject] = useState<ProjectOut | null>(null);
  const [pipelines, setPipelines] = useState<PipelineOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { data: me } = useMe();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [proj, pipes] = await Promise.all([
        getProject(token, projectId),
        listPipelines(token, projectId),
      ]);
      setProject(proj);
      setPipelines(pipes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load project">
        {error}
      </Alert>
    );
  }
  if (!project) return null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs" align="center" wrap="nowrap">
            <Title order={2}>{project.name}</Title>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Rename project"
              onClick={() => setRenaming(true)}
            >
              <IconPencil size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Share project"
              onClick={() => setSharing(true)}
            >
              <IconShare size={16} />
            </ActionIcon>
          </Group>
          {project.description && (
            <Text c="dimmed" size="sm">
              {project.description}
            </Text>
          )}
          <Text c="dimmed" size="xs" mt={4}>
            Created by {creator(project)} · {createdOn(project.created_at_utc)}
          </Text>
        </div>
        <Button
          component={RouterLink}
          to={`/projects/${projectId}/pipelines/new`}
          leftSection={<IconPlus size={16} />}
        >
          New pipeline
        </Button>
      </Group>

      <ShareDialog
        opened={sharing}
        onClose={() => setSharing(false)}
        resourceType="project"
        resourceId={projectId}
        resourceName={project.name}
        // Only the owner may grant. The dialog still opens for everyone else —
        // seeing who a thing is shared with is not the same as changing it.
        canShare={Boolean(me && project.created_by_user_id === me.user_id)}
      />

      <RenameModal
        opened={renaming}
        onClose={() => setRenaming(false)}
        what="project"
        name={project.name}
        description={project.description}
        onSubmit={async (name, description) => {
          const updated = await renameProject(await getToken(), projectId, name, description);
          setProject(updated);
        }}
      />

      <Title order={4}>Pipelines</Title>
      {pipelines.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No pipelines yet. Create your first one.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
          {pipelines.map((p) => (
            // The top edge carries the pipeline's LIFECYCLE colour, not the brand
            // colour: on a grid of pipelines the useful thing to see without
            // reading is which ones are in Production.
            <Card
              key={p.pipeline_id}
              withBorder
              radius="md"
              padding="lg"
              style={{
                ...listCardStyle,
                borderTop: `3px solid var(--mantine-color-${lifecycleColor(p.lifecycle_state)}-5)`,
              }}
            >
              <Group justify="space-between" mb="xs" wrap="nowrap">
                <Text fw={600} truncate>
                  {p.name}
                </Text>
                <Badge color={lifecycleColor(p.lifecycle_state)} variant="light" tt="capitalize">
                  {p.lifecycle_state}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" lineClamp={2}>
                {p.description ?? "No description"}
              </Text>
              <Text size="xs" c="dimmed" mt="sm">
                {p.latest_version_no != null ? `Version ${p.latest_version_no}` : "No versions yet"}
              </Text>
              <Text size="xs" c="dimmed" mt={2} lineClamp={2}>
                Created by {creator(p)} · {createdOn(p.created_at_utc)}
              </Text>
              <Button
                mt="md"
                style={listCardActionStyle}
                variant="light"
                size="xs"
                component={RouterLink}
                to={`/projects/${projectId}/pipelines/${p.pipeline_id}`}
              >
                Open pipeline
              </Button>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
